import { forwardRef, memo } from 'react';
import classNames from 'classnames';

import { SplashContent } from '../SplashContent/SplashContent';
import { SplashImage } from '../SplashImage/SplashImage';

import type { ReactNode } from 'react';
import type { ImageSources } from '../../../helpers/images';

import * as styles from './Splash.module.scss';

interface SplashProps {
  imageUrl?: string;
  previewUrl?: string;
  title: string;
  description?: string | null;
  className?: string;
  badge?: ReactNode;
  lazy?: boolean;
  hidden?: boolean;
  onPreviewEnded?: () => void;
  subtitle?: ReactNode;
  buttons?: ReactNode | ReactNode[];
  buttonsHelperText?: ReactNode | ReactNode[];
  isPulledUp?: boolean;
  labels?: (string | undefined | ReactNode)[];
  verticalImageSources?: ImageSources;
  redirectImageUrl?: string;
  variant?: 'base' | 'carousel';
}

const SplashBase = forwardRef<HTMLDivElement, SplashProps>(
  (
    {
      previewUrl: previewUrlProp,
      imageUrl,
      title,
      description,
      className,
      lazy = true,
      hidden,
      subtitle,
      onPreviewEnded,
      buttons,
      buttonsHelperText,
      isPulledUp,
      labels,
      badge,
      verticalImageSources,
      redirectImageUrl,
      variant,
    },
    ref
  ) => {
    const displayImage = Boolean(imageUrl);

    const splashClassName = classNames(
      styles.splash,
      {
        [styles.splashWithImage]: displayImage,
        [styles.splashWithNoImages]: !displayImage,
        [styles.isPulledUp]: isPulledUp,
      },
      className
    );

    return (
      <div className={splashClassName} aria-hidden={hidden} ref={ref}>
        {displayImage && (
          <a className={styles.imageWrapper} href={redirectImageUrl}>
            <SplashImage
              onPreviewEnded={onPreviewEnded}
              previewUrlProp={previewUrlProp}
              lazy={lazy}
              imageUrl={imageUrl}
              verticalImageSources={verticalImageSources}
              variant={variant}
            ></SplashImage>
          </a>
        )}
        <div className={classNames(styles.splashContent)}>
          <SplashContent
            title={title}
            subtitle={subtitle}
            description={description}
            buttons={buttons}
            buttonsHelperText={buttonsHelperText}
            badge={badge}
            verticalImageSources={verticalImageSources}
            variant={variant}
            labels={labels}
          ></SplashContent>
        </div>
      </div>
    );
  }
);
SplashBase.displayName = 'SplashBase';

export const Splash = memo(SplashBase);
